import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { BarChart } from '../../../../../components/muiLegacy/Charts/BarChart';
import { AnalysisChartLoading } from '../../../AnalysisChartLoading';
import { DomainTypeGq } from '../../../../../graphql/projects/models/DomainTypeGq';
import { projectsApiService } from '../../../../../graphql/projects/projectsApiService';
import { ChartProps } from '../../../AnalysisList';
import { internationalText } from '../../../internationalText';
import { rankIntervals, rankSegmentNames } from '../../../IntervalsAndColorsLogic';
import DataTimedOut, { didQueryTimeout } from '../../DataTimedOut';
import NotEnoughData from '../../NotEnoughData';
import NotEnoughTagGroups from '../../NotEnoughTagGroups';
import { getBarValues } from './RanksPerGroupVolumeChart.logic';

export interface RanksPerGroupVolumeChartProps extends ChartProps {}

const RanksPerGroupVolumeChart: React.FC<RanksPerGroupVolumeChartProps> = ({
  project,
  analysisFilterState,
  setAnalysisFilterState,
  language,
  palette,
}) => {
  // Adjust shown filters
  useEffect(
    () =>
      setAnalysisFilterState((prev) => ({
        ...prev,
        shownFields: { showTagGroupIds: true },
      })),
    []
  );

  // Retrieve data
  const analysisQuery = projectsApiService.queries.analysis.getRankIntervalsPerTagGroup({
    projectId: project.id,
    rankIntervals: rankIntervals,
    tagGroupIds: analysisFilterState?.tagGroupIds,
  });

  const loading = analysisQuery?.query?.loading;
  const result = analysisQuery?.result;

  // Transform data
  const bars = useMemo(() => getBarValues(result?.tagGroupAnalyses ?? []), [result]);

  // Render
  if (didQueryTimeout(analysisQuery?.query)) {
    return <DataTimedOut />;
  }

  if (loading) {
    return <AnalysisChartLoading />;
  }

  if (!analysisFilterState?.tagGroupIds?.length) {
    return <NotEnoughTagGroups />;
  }

  if (!result?.tagGroupAnalyses?.some((tg) => tg.intervalAnalyses?.some((iv) => iv.keywordCount))) {
    return <NotEnoughData />;
  }

  return (
    <BarChart
      colors="stoplight"
      palette={palette}
      leftLegend={internationalText.chart.topic_demand_ranking_distribution.legend[language]}
      segmentLegend
      ResponsiveBarProps={{
        data: bars,
        keys: rankSegmentNames,
        indexBy: 'barName',
      }}
    />
  );
};

export default RanksPerGroupVolumeChart;
